import { FC } from 'react';
import { useLanguage } from '@/contexts';
import { useComments } from '@/hooks/useComments';

interface Props {
  promptId: number;
  isLoggedIn: boolean;
  onAuthRequired: () => void;
  currentUserId: string;
}

const CommentsSection: FC<Props> = ({ promptId, isLoggedIn, onAuthRequired, currentUserId }) => {
  const { comments, commentsLoading, newCommentContent, statusMessage, setNewCommentContent, handleAddComment, handleCommentVote } = useComments({ promptId, isLoggedIn, currentUserId });
  const { t } = useLanguage();

  return (
    <div className="mt-8 pt-2 border-t border-gray-200 dark:border-slate-700">
      <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-4">{t("comments")}</h3>

      {statusMessage && (<p className="text-center text-sm mb-4 text-brand-electric-blue dark:text-blue-400" role="status" aria-live="polite">{statusMessage}</p>)}

      {isLoggedIn ? (
        <form onSubmit={handleAddComment} className="mb-6">
          <textarea
            className="w-full p-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-electric-blue dark:focus:ring-blue-400 placeholder-gray-500 dark:placeholder-slate-400 resize-y transition-colors duration-200"
            rows={3}
            placeholder={t("commentInputPlaceholder")}
            value={newCommentContent}
            onChange={(e) => setNewCommentContent(e.target.value)}
            aria-label="Comment input"
          />
          <button
            type="submit"
            className="mt-2 bg-brand-dark-blue dark:bg-blue-600 hover:bg-brand-electric-blue dark:hover:bg-blue-500 text-white font-medium py-2 px-4 rounded-md shadow-md transition-colors duration-200"
            aria-label="Post comment"
          >
            {t("commentPost")}
          </button>
        </form>
      ) : (
        // Prompt to log in to leave a comment 
        <div className="mb-6 p-4 bg-brand-light-gray dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-brand-dark-blue dark:text-slate-100 rounded-md text-center transition-colors duration-200">
          {t("please")} <button onClick={onAuthRequired} className="font-semibold underline hover:opacity-80">{t("logIn")}</button> {t("or")} <button onClick={onAuthRequired} className="font-semibold underline hover:opacity-80">{t("register")}</button> {t("leaveComment")}
        </div>
      )}

      {commentsLoading ?
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-electric-blue dark:border-blue-400 mx-auto"></div>
        </div>
        :
        <div className="space-y-4">
          {comments.length > 0 ? (
            comments.map((comment) => {
              const authorName = `${comment.first_name || ''} ${comment.last_name ? comment.last_name.charAt(0) : ''}`.trim();
              return (
                <div key={comment.id} className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-gray-800 dark:text-slate-100">{`${authorName}` || `User ${comment.user_id}`}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">{new Date(comment.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-gray-700 dark:text-slate-300 text-sm mb-3">{comment.content}</p>
                  <div className="flex items-center space-x-3">
                    {/* Upvote button for comments  */}
                    <button
                      onClick={() => handleCommentVote(comment.id, 1)}
                      className={`flex items-center text-sm transition-colors duration-200 ${comment.myVote === 1 ? 'text-brand-electric-blue dark:text-blue-400' : 'text-gray-500 dark:text-slate-400 hover:text-brand-electric-blue dark:hover:text-blue-400'}`}
                      aria-label={`Upvote comment by ${authorName}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                      </svg>
                      {t("upVote")}
                    </button>
                    <span className="text-sm font-semibold text-gray-800 dark:text-slate-200">{comment.votes_count}</span>
                    {/* Downvote button for comments */}
                    <button
                      onClick={() => handleCommentVote(comment.id, -1)}
                      className={`flex items-center text-sm transition-colors duration-200 ${comment.myVote === -1 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400'}`}
                      aria-label={`Downvote comment by ${authorName}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                      {t("downVote")}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500 dark:text-slate-400 text-center">{t("noComments")}</p>
          )}
        </div>
      }
    </div>
  );
};

export default CommentsSection;
